/**
 * <InstancedMoverBatch> — renders all movers of the same GLB type as a
 * single InstancedMesh. Reduces draw calls from N to 1 per mover-type.
 *
 * Per-instance transforms are updated imperatively every frame via the
 * MoverPositionStore. Per-instance colors encode selection state.
 *
 * Children (tools, custom assets) and hit-testing groups are still
 * rendered individually for event handling.
 */

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Color,
  DynamicDrawUsage,
  InstancedMesh,
  Mesh,
  Object3D,
} from 'three';
import type { BufferGeometry, Material } from 'three';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import type { GLTF } from 'three-stdlib';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import { moverWorldAt } from '../../geometry/ChainBuilder.js';
import { MoverPositionStoreContext } from './MoverPositionStore.js';
import { userToChainPositionMm } from '../../geometry/positionFrame.js';
import type { PositionFrame } from '../../types.js';

const _obj = new Object3D();
const _color = new Color();

interface MoverInstance {
  moverId: number;
  partPositionMm: number;
  chain: BuiltChain;
  selected: boolean;
  positionFrame?: PositionFrame;
}

interface InstancedMoverBatchProps {
  glbUrl: string;
  movers: MoverInstance[];
  selectionColor?: string;
  defaultColor?: string;
}

export const InstancedMoverBatch: React.FC<InstancedMoverBatchProps> = ({
  glbUrl,
  movers,
  selectionColor = '#90E0FF',
  defaultColor = '#ffffff',
}) => {
  const gltf = useGLTF(glbUrl) as GLTF;
  const meshRef = useRef<InstancedMesh>(null);
  const positionStore = useContext(MoverPositionStoreContext);

  const { geometry, material } = useMemo(() => {
    const scene = cloneSkeleton(gltf.scene);
    let geo: BufferGeometry | null = null;
    let mat: Material | Material[] | null = null;
    scene.traverse((obj) => {
      const m = obj as Mesh;
      if (m.isMesh && !geo) {
        geo = m.geometry;
        mat = m.material;
      }
    });
    return { geometry: geo, material: mat };
  }, [gltf]);

  const count = movers.length;

  useEffect(() => {
    const inst = meshRef.current;
    if (!inst) return;
    inst.instanceMatrix.setUsage(DynamicDrawUsage);
    if (inst.instanceColor) {
      inst.instanceColor.setUsage(DynamicDrawUsage);
    }
    inst.castShadow = true;
    inst.receiveShadow = true;
  }, [count]);

  useFrame(() => {
    const inst = meshRef.current;
    if (!inst) return;

    let dirty = false;
    for (let i = 0; i < movers.length; i++) {
      const mover = movers[i]!;
      const userPos =
        mover.moverId !== undefined && positionStore?.has(mover.moverId)
          ? (positionStore.get(mover.moverId) as number)
          : mover.partPositionMm;
      const chainPos = userToChainPositionMm(userPos, mover.positionFrame);
      const pose = moverWorldAt(mover.chain, chainPos);
      if (!pose) continue;

      _obj.position.set(pose.positionMm[0], pose.positionMm[1], pose.positionMm[2]);
      _obj.rotation.set(0, 0, (pose.yawDeg * Math.PI) / 180);
      _obj.updateMatrix();
      inst.setMatrixAt(i, _obj.matrix);

      _color.set(mover.selected ? selectionColor : defaultColor);
      inst.setColorAt(i, _color);

      dirty = true;
    }

    if (dirty) {
      inst.instanceMatrix.needsUpdate = true;
      if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
      inst.computeBoundingSphere();
    }
  });

  if (!geometry || !material || count === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material as Material, count]}
      frustumCulled={false}
    />
  );
};
